import { ReactNode, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { BodyText, Card, CardContent, HeadingText } from "../../../shared/ui";
import { KybComplianceDetail, KybDirector } from "../types";
import { FieldBlock } from "./kybShowReviewShared";

type KybComplianceOwnershipCardProps = {
  detail: KybComplianceDetail;
};

type OwnershipTab = "ownership" | "other-details";

const countryAccentClassNames: Record<string, string> = {
  Nigeria:
    "bg-[linear-gradient(90deg,#119B56_0%,#119B56_33%,#FFFFFF_33%,#FFFFFF_66%,#119B56_66%,#119B56_100%)]",
  UK: "bg-[#1D4ED8]",
  US: "bg-[#DC2626]",
  Ghana: "bg-[linear-gradient(180deg,#EF4444_0%,#EF4444_33%,#FACC15_33%,#FACC15_66%,#16A34A_66%,#16A34A_100%)]",
};

const formatPersonName = (person: KybDirector | null) => {
  if (!person) return "-";

  return [person.firstName, person.lastName].filter(Boolean).join(" ") || "-";
};

const formatValue = (value?: string) => {
  if (!value || value === "-") return "-";
  return value;
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <FieldBlock label={label} value={value || "-"} />
);

const PersonDetails = ({
  person,
  includeOwnership,
}: {
  person: KybDirector | null;
  includeOwnership?: boolean;
}) => (
  <div className="space-y-5">
    <DetailRow label="Name" value={formatValue(formatPersonName(person))} />
    <DetailRow label="Email address" value={formatValue(person?.email)} />
    <DetailRow
      label="Phone number"
      value={
        <div className="flex items-center gap-2">
          <span>{formatValue(person?.phoneNumber)}</span>
          {person?.phoneVerified ? (
            <span className="grid h-4 w-4 place-items-center rounded-full bg-[#E8FBE8] text-[#1DCB1C]">
              <Check size={12} />
            </span>
          ) : null}
        </div>
      }
    />
    <DetailRow label="Date of birth" value={formatValue(person?.dateOfBirth)} />
    {includeOwnership ? (
      <>
        <DetailRow
          label="Ownership percentage"
          value={formatValue(person?.percentageOwnership)}
        />
        <DetailRow
          label="Nature of control"
          value={formatValue(person?.natureOfControl)}
        />
      </>
    ) : null}
    <DetailRow label="BVN" value={person?.bvnNumber || "-"} />
    <DetailRow
      label="ID number"
      value={
        person?.idNumber ? (
          <span>
            {person.identificationType || "ID"}{" "}
            <span className="text-[#6F739B]">• {person.idNumber}</span>
          </span>
        ) : (
          "-"
        )
      }
    />
    {includeOwnership ? (
      <DetailRow
        label="Nationality"
        value={
          <div className="flex items-center gap-2">
            <span
              className={`h-4 w-4 rounded-full ${countryAccentClassNames[person?.nationality ?? ""] ?? "bg-[#EEF1FB]"}`}
            />
            <span>{person?.nationality || "-"}</span>
          </div>
        }
      />
    ) : (
      <DetailRow label="Title" value={person?.title || "-"} />
    )}
    <DetailRow label="Gender" value={person?.gender || "-"} />
    <DetailRow
      label="Residential address"
      value={person?.residentialAddress || "-"}
    />
  </div>
);


const KybComplianceOwnershipCard = ({
  detail,
}: KybComplianceOwnershipCardProps) => {
  const [activeTab, setActiveTab] = useState<OwnershipTab>("ownership");
  const [selectedDirectorIndex, setSelectedDirectorIndex] = useState(0);
  const [selectedUboIndex, setSelectedUboIndex] = useState(0);
  const directorCount = detail?.directors?.length || 0;
  const uboCount = detail?.ubos?.length || 0;
  const selectedDirector = detail?.directors?.[selectedDirectorIndex] ?? null;
  const selectedUbo = detail?.ubos?.[selectedUboIndex] ?? null;
  const canGoToPreviousDirector = selectedDirectorIndex > 0;
  const canGoToNextDirector = selectedDirectorIndex < directorCount - 1;
  console.log({ detail })

  useEffect(() => {
    setSelectedDirectorIndex((currentIndex) =>
      directorCount === 0 ? 0 : Math.min(currentIndex, directorCount - 1),
    );
  }, [directorCount]);

  useEffect(() => {
    setSelectedUboIndex((currentIndex) =>
      uboCount === 0 ? 0 : Math.min(currentIndex, uboCount - 1),
    );
  }, [uboCount]);

  return (
    <Card className="overflow-hidden rounded-[12px] border border-[#E1E6FF] bg-[#FBFCFE] shadow-none">
      <CardContent className="p-6">
        <div className="grid grid-cols-2 rounded-[10px] bg-[#F3F4FB] p-1">
          {[
            { label: "Ownership", value: "ownership" },
            { label: "Other details", value: "other-details" },
          ].map((tab) => {
            const isActive = activeTab === tab.value;

            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value as OwnershipTab)}
                className={`rounded-[7px] px-3 py-2 aku-body-small-medium transition-colors ${
                  isActive
                    ? "bg-white text-[#161827] shadow-[0_2px_8px_rgba(15,20,40,0.14)]"
                    : "text-[#6F739B] hover:text-[#161827]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === "ownership" ? (
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <HeadingText className="aku-body-small-medium text-[#161827]">
                Ultimate Beneficial Owner
              </HeadingText>
              {uboCount > 1 ? (
                <BodyText className="aku-body-small-regular text-[#6F739B]">
                  {selectedUboIndex + 1}/{uboCount}
                </BodyText>
              ) : null}
            </div>

            <div className="mt-5">
              <PersonDetails person={selectedUbo} includeOwnership />
            </div>

            <div className="my-6 h-px bg-[#E4E7F4]" />

            <div className="flex items-center justify-between">
              <HeadingText className="aku-body-small-medium text-[#161827]">
                Directors{" "}
                <span className="aku-body-small-regular text-[#6F739B]">
                  {directorCount === 0
                    ? "0/0"
                    : `${selectedDirectorIndex + 1}/${directorCount}`}
                </span>
              </HeadingText>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!canGoToPreviousDirector}
                  onClick={() =>
                    setSelectedDirectorIndex((currentIndex) =>
                      Math.max(currentIndex - 1, 0),
                    )
                  }
                  className="grid h-8 w-8 place-items-center rounded-[8px] border border-[#E4E7F4] text-[#6F739B] hover:bg-[#F7F8FD] disabled:cursor-not-allowed disabled:text-[#C0C5E3] disabled:hover:bg-transparent"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  disabled={!canGoToNextDirector}
                  onClick={() =>
                    setSelectedDirectorIndex((currentIndex) =>
                      Math.min(currentIndex + 1, directorCount - 1),
                    )
                  }
                  className="grid h-8 w-8 place-items-center rounded-[8px] border border-[#E4E7F4] text-[#6F739B] hover:bg-[#F7F8FD] disabled:cursor-not-allowed disabled:text-[#C0C5E3] disabled:hover:bg-transparent"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="mt-5">
              <PersonDetails person={selectedDirector} />
            </div>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
              <FieldBlock label="Web presence" value={formatValue(detail?.businessWebsite)} />
            <FieldBlock
              label="Transaction volume"
                value={formatValue(detail?.transactionVolume)}
            />
            <FieldBlock
              label="Source of funds"
              value={formatValue(detail.sourceOfFunds)}
              />
              {detail?.additionalInformation?.map(({ label, value }) => {
                return <FieldBlock
                  label={label}
                  value={formatValue(value)}
                />
              })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KybComplianceOwnershipCard;
